/**
 * Size & Paper domain logic — pure helpers behind screen 2a, the crop editor
 * (1d), the review tray (2b) and the mobile size picker (1h). No I/O, no DOM,
 * no store imports (the store imports FROM here), so everything is unit-testable.
 *
 * Covers:
 *  - display helpers (size / ratio / paper-chip labels)
 *  - grouped option lists for the size picker and the fine-art paper picker
 *  - size↔paper compatibility (C-Type finishes vs fine-art vs size-gated specialty)
 *  - the "largest size that stays ≥ the family's minimum DPI" swap for blocked prints
 *  - print/photo Print-Check status aggregation (flags, blocked, resolved)
 *
 * Resolution semantics (README "Print Check thresholds" + build spec):
 *  - a print with printCheck 'lowres' and no override is BLOCKED — it stays in
 *    the order but is excluded from totals until swapped/removed/overridden.
 *  - a ratio-mismatch print is "unresolved" while it still has the silent
 *    default (fitMode 'crop', cropRect null). Choosing Crop (which bakes the
 *    centered rect into cropRect), Fit, or a custom crop resolves it.
 */

import {
  borderModesFor,
  ctypeSizeId,
  fineArtSizeId,
  frameColours,
  frameConfigKey,
  framedProduct,
  framedProductList,
  papersForSize,
  parseSizeId,
  priceFor,
  resolveBorder,
  type BorderMode,
  type FramedProduct,
  type PaperId,
  type PricingData,
  type ResolvedSize,
} from '@rpl/pricing';
import { customSizeLabel, isCustomSizeId } from './customSize';
import { formatMoney } from './format';
import { priceDeltaLabel } from './upsell';
import {
  centeredAutoCropRect,
  checkPrint,
  DEFAULT_THRESHOLDS,
  effectiveDpi,
  minDpiOf,
  ratioFit,
  type Inches,
  type PrintCheckThresholds,
  type PrintFamily,
} from './printCheck';
import { rotatedDims, type Rotation } from './rotate';
import type { CropRect, LocalPhoto, SortMode, UiPrint } from './types';

/* ============================================================
   Display helpers
   ============================================================ */

/** `'4x6'` → `'4×6'`, `'3.5x5'` → `'3.5×5'`, `'A3'` → `'A3'` (chip/badge short form). */
export function sizeShort(size: string): string {
  return size.includes('x') ? size.split('x').join('×') : size;
}

/**
 * Size with its unit for display: 16x24 -> 16×24″, but A3 -> A3.
 *
 * A-series sizes are not inches, so appending ″ produced "A3″ print in A2″
 * frame" the moment the A2 frame shipped (owner 2026-08-28). Callers used to
 * hardcode the mark after sizeShort(); use this instead.
 */
export function sizeWithUnit(size: string): string {
  const short = sizeShort(size);
  return /^A\d$/i.test(size) ? short : `${short}″`;
}

/** Common photographic aspect ratios (long/short) with friendly labels. */
const COMMON_RATIOS: ReadonlyArray<readonly [number, string]> = [
  [1, '1:1'],
  [7 / 6, '7:6'],
  [6 / 5, '6:5'],
  [5 / 4, '5:4'],
  [4 / 3, '4:3'],
  [7 / 5, '7:5'],
  [10 / 7, '10:7'],
  [3 / 2, '3:2'],
  [8 / 5, '8:5'],
  [5 / 3, '5:3'],
  [16 / 9, '16:9'],
  [2, '2:1'],
  [5 / 2, '5:2'],
  [3, '3:1'],
  [4, '4:1'],
];

/**
 * Friendly aspect-ratio label for arbitrary dimensions, orientation-agnostic
 * (always long:short). Snaps to the nearest common photographic ratio when
 * within 2.5%, else falls back to a decimal form like `1.44:1`.
 */
export function ratioLabel(w: number, h: number): string {
  const r = Math.max(w, h) / Math.min(w, h);
  let best: readonly [number, string] | null = null;
  let bestErr = Infinity;
  for (const c of COMMON_RATIOS) {
    const err = Math.abs(r - c[0]) / c[0];
    if (err <= 0.025 && err < bestErr) {
      best = c;
      bestErr = err;
    }
  }
  return best ? best[1] : `${r.toFixed(2)}:1`;
}

/** Ratio label for a print size, e.g. `[4,6]` → `'3:2'`, `[8,10]` → `'5:4'`. */
export function printRatioLabel(inches: Inches): string {
  return ratioLabel(inches[0], inches[1]);
}

/**
 * Short paper names for print chips ("4×6 (1) Gloss"). Display abbreviations
 * only — prices/availability always come from PricingData. Unknown papers fall
 * back to their full pricing label.
 */
/* Owner 2026-07-27: consistent family prefixes — "Matt" beside "B. Lustre"
   read like two different naming schemes. */
const PAPER_SHORT: Partial<Record<PaperId, string>> = {
  gloss: 'Gloss',
  lustre: 'Lustre',
  'bauhaus-matt': 'Bauhaus Matt',
  'bauhaus-lustre': 'Bauhaus Lustre',
  'bauhaus-metallic': 'Bauhaus Metallic',
  'ilford-pearl': 'Ilford Pearl',
  'ilford-cottonrag-smooth': 'Cotton Rag Smooth',
  'ilford-cottonrag-textured': 'Cotton Rag Textured',
};

export function shortPaperLabel(data: PricingData, paper: PaperId): string {
  return PAPER_SHORT[paper] ?? data.papers[paper]?.label ?? paper;
}

/** Paper names for FRAMED upsell labels/warnings — unambiguous next to border
 *  wording ("Matt" alone reads as the matt board), yet shorter than the full
 *  catalogue labels ("Bauhaus Photo Matt 250gsm"). */
const FRAME_PAPER_LABEL: Partial<Record<PaperId, string>> = {
  gloss: 'Gloss',
  lustre: 'Lustre',
  'bauhaus-matt': 'Bauhaus Matt',
  'bauhaus-lustre': 'Bauhaus Lustre',
  'bauhaus-metallic': 'Metallic',
  'ilford-pearl': 'Ilford Pearl',
  'ilford-cottonrag-smooth': 'Cotton Rag Smooth',
  'ilford-cottonrag-textured': 'Cotton Rag Textured',
};
export function framePaperLabel(data: PricingData, paper: PaperId): string {
  return FRAME_PAPER_LABEL[paper] ?? data.papers[paper]?.label ?? paper;
}

/** `ct-4x6` → `4×6`; custom sizes read `Custom 11.5×17`. */
export function sizeIdShort(sizeId: string): string {
  const size = sizeShort(parseSizeId(sizeId).size);
  return isCustomSizeId(sizeId) ? `Custom ${size}` : size;
}

/** Chip text for one print: `4×6 (2) Gloss` (blocked chips omit the paper). */
export function chipLabel(data: PricingData, p: UiPrint): string {
  const size = sizeIdShort(p.sizeId);
  if (isBlockedPrint(p)) return `${size} (${p.qty})`;
  return `${size} (${p.qty}) ${shortPaperLabel(data, p.paper)}`;
}

/**
 * Small italic border tag appended to a chip: `WB` (white border) / `HB`
 * (handling border); null for print-to-edge and framed prints (round-4:
 * replaces the cryptic ▢ / ⌗ glyphs).
 */
export function chipBorderTag(p: UiPrint): 'WB' | 'HB' | null {
  if (p.frameId) return null;
  const b = p.borderMode ?? 'none';
  return b === 'white' ? 'WB' : b === 'handling' ? 'HB' : null;
}

/* ============================================================
   Size ↔ paper compatibility
   ============================================================ */

export function isCtPaper(data: PricingData, paper: PaperId): boolean {
  return data.papers[paper]?.priceTier === 'ctype';
}

function isSpecialty(data: PricingData, paper: PaperId): boolean {
  return data.papers[paper]?.priceTier === 'specialty';
}

/** Raw sizes ("A1") a specialty paper is offered in. */
export function specialtySizesFor(data: PricingData, paper: PaperId): string[] {
  return data.productLines.fine_art.specialty.filter((s) => s.paper === paper).map((s) => s.size);
}

/** Default C-Type finish / fine-art paper — first of each line's paper list. */
export function defaultCtPaper(data: PricingData): PaperId {
  return data.productLines.ctype_standard.papers[0] ?? 'gloss';
}
export function defaultFaPaper(data: PricingData): PaperId {
  return data.productLines.fine_art.papers[0] ?? 'bauhaus-matt';
}

export function isPaperValidForSize(data: PricingData, sizeId: string, paper: PaperId): boolean {
  const parsed = parseSizeId(sizeId);
  if (parsed.family === 'ctype') {
    if (!isCtPaper(data, paper)) return false;
    // Per-size finish restriction (pricing v9, e.g. 8×12 prints on Lustre only).
    try {
      return papersForSize(data, sizeId).includes(paper);
    } catch {
      return true; // stale/unknown C-Type size — the family check above is all we have
    }
  }
  if (isCtPaper(data, paper)) return false;
  if (isSpecialty(data, paper)) return specialtySizesFor(data, paper).includes(parsed.size);
  return true;
}

/**
 * The allowed finishes for a C-Type size that carries a per-size restriction
 * (pricing v9 sizes[].papers — e.g. 8×12 → [lustre]); null when the size
 * offers every finish of its line, isn't C-Type, or is unknown.
 */
export function restrictedCtPapers(data: PricingData, sizeId: string): PaperId[] | null {
  try {
    const parsed = parseSizeId(sizeId);
    if (parsed.family !== 'ctype') return null;
    const allowed = papersForSize(data, sizeId);
    const line = (['ctype_standard', 'ctype_enlargements'] as const)
      .map((id) => data.productLines[id])
      .find((l) => l.sizes.some((s) => s.size === parsed.size));
    if (!line) return null;
    return allowed.length > 0 && allowed.length < line.papers.length ? allowed : null;
  } catch {
    return null;
  }
}

/**
 * Small note for a restricted C-Type size, shown next to the finish control
 * and after an auto-correct: `8×12 prints on Lustre only`. Null when the size
 * has no restriction.
 */
export function paperRestrictionNote(data: PricingData, sizeId: string): string | null {
  const allowed = restrictedCtPapers(data, sizeId);
  if (!allowed) return null;
  const names = allowed.map((p) => data.papers[p]?.label ?? p).join(' or ');
  return `${sizeShort(parseSizeId(sizeId).size)} prints on ${names} only`;
}

/**
 * Paper that applies after switching to `sizeId`: the current paper when it is
 * valid for that size, else the family default (gloss for C-Type, the first
 * fine-art paper for fine art). A restricted C-Type size that excludes the
 * default falls to its first allowed finish (8×12 auto-corrects to Lustre).
 * Used when the size dropdown crosses families, hits a restricted size, or
 * leaves a size-gated specialty paper behind.
 */
export function paperForSize(data: PricingData, sizeId: string, current: PaperId): PaperId {
  if (isPaperValidForSize(data, sizeId, current)) return current;
  if (parseSizeId(sizeId).family !== 'ctype') return defaultFaPaper(data);
  const fallback = defaultCtPaper(data);
  try {
    const allowed = papersForSize(data, sizeId);
    if (allowed.length > 0) return allowed.includes(fallback) ? fallback : allowed[0]!;
  } catch {
    /* unknown size — family default below */
  }
  return fallback;
}

/** priceFor() that returns null instead of throwing for invalid combinations. */
export function priceForSafe(data: PricingData, sizeId: string, paper: PaperId): number | null {
  try {
    return priceFor(data, sizeId, paper);
  } catch {
    return null;
  }
}

/* ============================================================
   Picker option groups (size dropdown + fine-art paper dropdown)
   ============================================================ */

export interface PickerOption {
  key: string;
  label: string;
  /** Right-aligned text: a delta price ("+$6.00") or the words "Same price". */
  right?: string;
  /** Render `right` muted — used for "Same price", which must never read as a price. */
  rightMuted?: boolean;
  /** Small badge, e.g. "Fits your photo" / "Most popular". */
  badge?: string;
  /** Badge colour: green = fits, yellow = small crop, amber = needs crop. */
  badgeTone?: 'fit' | 'near' | 'crop' | 'popular';
  /** Secondary muted badge (e.g. "Popular" alongside a fit badge). */
  badge2?: string;
  /** Light-green hero row — the biggest size this photo supports. */
  highlight?: boolean;
  /** Inline hint after the label, e.g. "pano" or a specialty "<sizes> only". */
  hint?: string;
  disabled?: boolean;
}

export interface PickerGroup {
  label: string;
  options: PickerOption[];
}

/** Sentinel option key: "Custom size…" row in the Fine Art group (opens the dialog). */
export const CUSTOM_SIZE_OPTION_KEY = 'custom-size-new';

/** Sizes tagged "Most popular" in the size dropdown (social proof + smart
 * default; behavioral round 2026-07-12). */
const MOST_POPULAR_SIZE_IDS = new Set(['ct-4x6', 'ct-8x10']);

/**
 * Grouped size options for the size dropdown: Standard Prints / Enlargements &
 * Panoramas / Fine Art. Fine-art rows are priced for the paper that WOULD
 * apply if picked (the current paper when fine-art, else the default fine-art
 * paper) so the price shown always matches what selecting the row produces.
 * Session custom sizes (cu- catalog entries) list at the end of the Fine Art
 * group; `customAction: true` appends the "Custom size…" creator row there too.
 */
export function sizePickerGroups(
  data: PricingData,
  catalog: Map<string, ResolvedSize>,
  currentPaper: PaperId,
  opts?: {
    customAction?: boolean;
    currentSizeId?: string | null;
    /** Badge rows that fit this photo's aspect (owner 2026-08-15) — the same
     *  information as the fit list, wherever the customer happens to look. */
    fitPhoto?: { width: number; height: number } | null;
  },
): PickerGroup[] {
  /* Delta pricing (owner 2026-08-15): every row reads as its INCREMENT from
     the size currently selected ("8×10 · +$5.50"), the McDonald's "30c more
     for a large" mechanic. With nothing selected yet there is no reference
     point, so rows fall back to absolute prices. */
  const currentPrice =
    opts?.currentSizeId != null
      ? priceForSafe(data, opts.currentSizeId, paperForSize(data, opts.currentSizeId, currentPaper))
      : null;
  const std: PickerOption[] = [];
  const enl: PickerOption[] = [];
  const fa: PickerOption[] = [];
  const custom: PickerOption[] = [];
  for (const entry of catalog.values()) {
    const effPaper = paperForSize(data, entry.sizeId, currentPaper);
    const price = priceForSafe(data, entry.sizeId, effPaper);
    // Restricted C-Type sizes flag their only finishes inline ("Lustre only").
    const restricted = entry.family === 'ctype' ? restrictedCtPapers(data, entry.sizeId) : null;
    const restrictionHint = restricted
      ? `${restricted.map((p) => data.papers[p]?.label ?? p).join(' / ')} only`
      : null;
    /* Ratio badge at a glance (owner 2026-08-15): green fits / yellow small
       crop / amber needs crop, so nobody has to pick a size to find out. */
    const fitTone = (() => {
      const ph = opts?.fitPhoto;
      if (!ph) return null;
      const photoRatio = Math.max(ph.width, ph.height) / Math.min(ph.width, ph.height);
      const sizeRatio =
        Math.max(entry.inches[0], entry.inches[1]) / Math.min(entry.inches[0], entry.inches[1]);
      const off = Math.abs(sizeRatio - photoRatio) / photoRatio;
      /* Owner 2026-08-15 round 3: ONLY the green "fits" badge. Three colours
         across every row was technical noise on a decision the customer has
         not reached yet — cropping is raised later, on the print they chose. */
      return off <= 0.02 ? { badge: 'Fits your photo', tone: 'fit' as const } : null;
    })();
    const isCurrent = opts?.currentSizeId === entry.sizeId;
    // Cheaper rows show the muted ABSOLUTE price (owner 2026-08-16 revision:
    // hiding them forced manual price-hunting) — never a "−$X" saving pitch.
    // The CURRENT row shows its own muted absolute too (owner, same day): the
    // tick says "yours", the grey price keeps the row comparable to the rest.
    const delta = isCurrent
      ? price != null
        ? { text: formatMoney(price), muted: true }
        : null
      : priceDeltaLabel(price, currentPrice);
    const opt: PickerOption = {
      key: entry.sizeId,
      label: entry.label,
      // The selected row carries no price at all — its tick already says it.
      ...(delta ? { right: delta.text, ...(delta.muted ? { rightMuted: true } : {}) } : {}),
      ...(entry.pano ? { hint: 'pano' } : restrictionHint ? { hint: restrictionHint } : {}),
      /* The ratio badge is contextual truth so it takes the primary slot;
         "Most popular" rides along as a muted secondary when both apply. */
      ...(fitTone
        ? {
            badge: fitTone.badge,
            badgeTone: fitTone.tone,
            ...(MOST_POPULAR_SIZE_IDS.has(entry.sizeId) ? { badge2: 'Popular' } : {}),
          }
        : MOST_POPULAR_SIZE_IDS.has(entry.sizeId)
          ? { badge: 'Most popular', badgeTone: 'popular' as const }
          : {}),
    };
    if (isCustomSizeId(entry.sizeId)) custom.push(opt);
    else if (entry.productLine === 'ctype_standard') std.push(opt);
    else if (entry.productLine === 'ctype_enlargements') enl.push(opt);
    else fa.push(opt);
  }
  fa.push(...custom);
  if (opts?.customAction && data.customSizing?.enabled) {
    fa.push({ key: CUSTOM_SIZE_OPTION_KEY, label: 'Custom size…', hint: 'fine art' });
  }
  return [
    { label: 'Standard Prints', options: std },
    { label: 'Enlargements & Panoramas', options: enl },
    { label: 'Fine Art', options: fa },
  ];
}

/** Display headings for the fine-art paper picker groups (design copy). */
const PAPER_GROUP_LABELS: Record<string, string> = {
  standard: 'Standard — Bauhaus',
  fineArt: 'Fine Art — Ilford',
  specialty: 'Specialty',
};

/**
 * Grouped paper options for a FINE-ART size: Standard / Fine Art / Specialty.
 * Specialty rows (when the catalogue carries any — none since pricing v8) are
 * enabled only for the sizes they exist in and show a "<sizes> only" hint
 * otherwise; empty groups are omitted entirely. Every enabled row shows its
 * per-size price.
 */
export function paperPickerGroups(
  data: PricingData,
  sizeId: string,
  currentPaper?: PaperId | null,
): PickerGroup[] {
  /* Delta pricing against the paper in hand (owner 2026-08-15): a Bauhaus Matt
     customer sees Cottonrag as "+$11.00", not "$40.00". Equal-priced papers
     say "Same price" — never "$0.00", which reads as a free product. */
  const currentPrice =
    currentPaper != null ? priceForSafe(data, sizeId, currentPaper) : null;
  const groups: PickerGroup[] = [];
  const byGroup = new Map<string, PickerOption[]>();
  const push = (group: string, opt: PickerOption) => {
    if (!byGroup.has(group)) byGroup.set(group, []);
    byGroup.get(group)!.push(opt);
  };

  for (const paper of data.productLines.fine_art.papers) {
    const info = data.papers[paper];
    if (!info) continue;
    const price = priceForSafe(data, sizeId, paper);
    const delta =
      paper === currentPaper
        ? price != null
          ? { text: formatMoney(price), muted: true }
          : null
        : priceDeltaLabel(price, currentPrice);
    push(info.uiGroup, {
      key: paper,
      label: info.label,
      ...(delta ? { right: delta.text, ...(delta.muted ? { rightMuted: true } : {}) } : {}),
      ...(info.badge ? { badge: info.badge } : {}),
    });
  }

  // Specialty papers come from the explicit (paper, size) price entries.
  const seen = new Set<PaperId>();
  for (const entry of data.productLines.fine_art.specialty) {
    if (seen.has(entry.paper)) continue;
    seen.add(entry.paper);
    const info = data.papers[entry.paper];
    if (!info) continue;
    const sizes = specialtySizesFor(data, entry.paper);
    const enabled = sizes.includes(parseSizeId(sizeId).size);
    const price = enabled ? priceForSafe(data, sizeId, entry.paper) : null;
    const spDelta =
      entry.paper === currentPaper
        ? price != null
          ? { text: formatMoney(price), muted: true }
          : null
        : priceDeltaLabel(price, currentPrice);
    push(info.uiGroup, {
      key: entry.paper,
      label: info.label,
      ...(spDelta ? { right: spDelta.text, ...(spDelta.muted ? { rightMuted: true } : {}) } : {}),
      ...(info.badge ? { badge: info.badge } : {}),
      ...(enabled ? {} : { disabled: true, hint: `${sizes.map(sizeShort).join(' / ')} only` }),
    });
  }

  for (const key of ['standard', 'fineArt', 'specialty']) {
    const options = byGroup.get(key);
    if (options?.length) groups.push({ label: PAPER_GROUP_LABELS[key] ?? key, options });
  }
  return groups;
}

/* ============================================================
   Largest size that still prints ≥ the family's minimum DPI
   ============================================================ */

/**
 * The largest catalog size (by print area) in the SAME family as the blocked
 * size that would print at ≥ the family's minimum DPI with a centered
 * auto-crop, and that the given paper can actually be printed on. Powers
 * "Use 6×8" max". Returns null when nothing fits (caller then offers only
 * Remove / Override).
 */
export function largestFittingSize(
  data: PricingData,
  catalog: Map<string, ResolvedSize>,
  photo: { width: number; height: number },
  blockedSizeId: string,
  paper: PaperId,
  thresholds: PrintCheckThresholds = DEFAULT_THRESHOLDS,
): ResolvedSize | null {
  const family = parseSizeId(blockedSizeId).family;
  let best: ResolvedSize | null = null;
  let bestArea = -1;
  for (const entry of catalog.values()) {
    if (entry.family !== family) continue;
    if (entry.sizeId === blockedSizeId) continue;
    if (isCustomSizeId(entry.sizeId)) continue; // never swap INTO a custom size
    if (!isPaperValidForSize(data, entry.sizeId, paper)) continue;
    const result = checkPrint(photo, entry.inches, null, 'crop', entry.family, thresholds);
    if (result.status === 'lowres') continue;
    const area = entry.inches[0] * entry.inches[1];
    const longer = Math.max(entry.inches[0], entry.inches[1]);
    const bestLonger = best ? Math.max(best.inches[0], best.inches[1]) : -1;
    if (area > bestArea || (area === bestArea && longer > bestLonger)) {
      best = entry;
      bestArea = area;
    }
  }
  return best;
}

/* ============================================================
   Print / photo status aggregation
   ============================================================ */

/** Blocked: low-res and not overridden → excluded from totals, needs a decision. */
export function isBlockedPrint(p: UiPrint): boolean {
  return p.printCheck === 'lowres' && !p.overrideLowRes;
}

/** Prints that participate in pricing (blocked low-res excluded per the spec). */
export function pricedPrints(prints: UiPrint[]): UiPrint[] {
  return prints.filter((p) => !isBlockedPrint(p));
}

/**
 * Ratio mismatch still on the silent default (auto-crop, no explicit choice).
 * Resolved by: Fit, a confirmed/custom cropRect, or the size being blocked
 * (low-res dominates the flag).
 */
export function isRatioUnresolved(p: UiPrint): boolean {
  return p.printCheck === 'autocrop' && p.fitMode === 'crop' && p.cropRect === null;
}

export function isLowresUnresolved(p: UiPrint): boolean {
  return isBlockedPrint(p);
}

/** Needs attention in the Review tray. */
export function isFlaggedPrint(p: UiPrint): boolean {
  return isRatioUnresolved(p) || isLowresUnresolved(p);
}

/** A ratio-mismatch print whose crop/fit decision has been made. */
export function isRatioResolved(p: UiPrint): boolean {
  return p.printCheck === 'autocrop' && !isRatioUnresolved(p);
}

/** A print carrying an EXPLICIT crop decision (custom or confirmed auto-crop). */
export function hasCustomCrop(p: UiPrint): boolean {
  return p.fitMode === 'crop' && p.cropRect !== null;
}

/**
 * Whether the Configure under-preview warning ("this photo will be
 * auto-cropped") should show for a photo × size, given the print that Add /
 * Adjustments would actually touch (the loose merge target). Round-7 fix: the
 * warning used to key off the RAW ratio mismatch, so it kept nagging after the
 * customer saved a custom crop in the editor. It must key off the same
 * resolution rule as flaggedForReview: an explicit cropRect or Fit resolves
 * the print, so the warning clears; no print yet → warn purely on ratio.
 */
export function previewCropWarning(
  photo: { width: number; height: number },
  inches: Inches,
  existing: UiPrint | undefined,
  tolerance = DEFAULT_THRESHOLDS.ratioTolerance,
): boolean {
  const mismatch =
    photo.width > 0 &&
    photo.height > 0 &&
    ratioFit(photo.width, photo.height, inches, tolerance) === 'autocrop';
  if (!mismatch) return false;
  return existing ? isRatioUnresolved(existing) : true;
}

/**
 * Prints that still need a decision in the Review-crops modal / banner: loose
 * prints on the SILENT auto-crop default (no explicit crop/fit choice yet) or
 * blocked low-res. Confirming a crop (which bakes cropRect), choosing Fit,
 * adjusting in the editor, swapping, removing or "Print anyway" all resolve a
 * print, so it leaves this set — and the banner count — immediately.
 * (Round-3 fix: the old predicate ignored cropRect, so confirmed crops never
 * cleared the banner.)
 */
export function flaggedForReview(prints: UiPrint[]): UiPrint[] {
  // Framed prints review EXACTLY like loose ones (owner 2026-07-27): frames
  // now add straight from the Customise pane with a silent auto-crop, so an
  // aspect mismatch must surface in the review/Continue flow. (The old
  // framed-skip dated from the convert-a-loose-print dialog, which inherited
  // an already-confirmed crop.)
  return prints.filter((p) => isRatioUnresolved(p) || isBlockedPrint(p));
}

/**
 * Wizard → Review handoff (round 7): the ids of the prints a wizard apply just
 * created that need review (auto-crops on the silent default / blocked
 * low-res). The Easy Print Wizard passes this to the Review-crops dialog so it
 * opens SCOPED to the new prints — the customer reviews the wizard's crops
 * immediately instead of hunting for them.
 */
export function reviewScopeFor(touchedIds: readonly string[], prints: readonly UiPrint[]): string[] {
  const touched = new Set(touchedIds);
  return flaggedForReview(prints.filter((p) => touched.has(p.id))).map((p) => p.id);
}

/* ============================================================
   Review-crops SESSION (round 8 — see then confirm)
   ============================================================ */

/**
 * Seed the Review-crops dialog's SESSION set: the ids of the prints that need
 * attention at the moment the dialog opens, narrowed to `scopeIds` when the
 * wizard hands one over. Prints resolved BEFORE opening stay out (an old order
 * of confirmed crops doesn't flood the grid); prints resolved DURING the
 * session stay in — the whole point is seeing the applied outcome before Done.
 */
export function seedReviewSession(
  prints: UiPrint[],
  scopeIds?: readonly string[] | null,
): Set<string> {
  let flagged = flaggedForReview(prints);
  if (scopeIds) {
    const scope = new Set(scopeIds);
    flagged = flagged.filter((p) => scope.has(p.id));
  }
  return new Set(flagged.map((p) => p.id));
}

/**
 * The cards the dialog shows for a session, in order: every session print that
 * still exists. RESOLVED cards remain (their ✓ chip shows the saved outcome);
 * a print that was removed — or merged away by a swap/editor save — drops out,
 * because there is nothing left to show.
 */
export function sessionReviewCards(prints: UiPrint[], session: ReadonlySet<string>): UiPrint[] {
  return prints.filter((p) => session.has(p.id));
}

/** ✓ state-chip labels a resolved review card can carry. */
export type ReviewChip = 'Auto-crop' | 'Custom crop' | 'Fit' | 'Printing anyway' | 'No crop needed';

export interface ReviewCardState {
  /** blocked = low-res decision pending · needs-look = silent auto-crop · resolved = saved. */
  kind: 'blocked' | 'needs-look' | 'resolved';
  /** Chip text for resolved cards; null while the card still needs a decision. */
  chip: ReviewChip | null;
}

/** Does a saved rect (still) sit on the centered auto-crop? (±0.5% per field) */
function isCenteredAutoRect(
  rect: CropRect,
  photo: { width: number; height: number },
  inches: Inches,
  rotation: Rotation,
): boolean {
  const [rw, rh] = rotatedDims(photo.width, photo.height, rotation);
  const auto = centeredAutoCropRect(rw, rh, inches);
  const eps = 0.005;
  return (
    Math.abs(rect.x - auto.x) < eps &&
    Math.abs(rect.y - auto.y) < eps &&
    Math.abs(rect.w - auto.w) < eps &&
    Math.abs(rect.h - auto.h) < eps
  );
}

/**
 * What a review card shows for a print (round 8): still-open states first,
 * then the saved outcome — "Fit", a confirmed "Auto-crop" (rect on the centered
 * default), a dragged/edited "Custom crop", a low-res "Printing anyway"
 * override, or "No crop needed" (e.g. swapped to a size the photo fits exactly).
 */
export function reviewCardState(
  print: UiPrint,
  photo: { width: number; height: number } | undefined,
  inches: Inches | undefined,
): ReviewCardState {
  if (isBlockedPrint(print)) return { kind: 'blocked', chip: null };
  if (isRatioUnresolved(print)) return { kind: 'needs-look', chip: null };
  if (print.printCheck === 'lowres' && print.overrideLowRes) {
    return { kind: 'resolved', chip: 'Printing anyway' };
  }
  if (print.fitMode === 'fit') return { kind: 'resolved', chip: 'Fit' };
  if (print.cropRect) {
    const auto =
      photo && photo.width > 0 && photo.height > 0 && inches
        ? isCenteredAutoRect(print.cropRect, photo, inches, print.rotation ?? 0)
        : false;
    return { kind: 'resolved', chip: auto ? 'Auto-crop' : 'Custom crop' };
  }
  return { kind: 'resolved', chip: 'No crop needed' };
}

export interface ContinueGateSummary {
  /** Unresolved auto-crop prints / distinct photos. */
  ratioPrints: number;
  ratioPhotos: number;
  /** Blocked low-res prints (excluded from the order until resolved). */
  lowresPrints: number;
  total: number;
  /** e.g. "3 photos will be auto-cropped · 1 low-quality print left out". */
  label: string;
}

/**
 * Summary for the Continue confirm dialog when attention items are unresolved.
 * Returns null when nothing needs attention (Continue proceeds directly).
 */
export function continueGateSummary(prints: UiPrint[]): ContinueGateSummary | null {
  const flagged = flaggedForReview(prints);
  if (flagged.length === 0) return null;
  const ratio = flagged.filter(isRatioUnresolved);
  const lowres = flagged.filter(isBlockedPrint);
  const ratioPhotos = new Set(ratio.map((p) => p.photoId)).size;
  const parts: string[] = [];
  if (ratioPhotos > 0) {
    parts.push(`${ratioPhotos} photo${ratioPhotos === 1 ? '' : 's'} will be auto-cropped`);
  }
  if (lowres.length > 0) {
    parts.push(`${lowres.length} low-quality print${lowres.length === 1 ? '' : 's'} left out`);
  }
  return {
    ratioPrints: ratio.length,
    ratioPhotos,
    lowresPrints: lowres.length,
    total: flagged.length,
    label: parts.join(' · '),
  };
}

/**
 * Worst-of status for a photo's card ring / legend bucket:
 *  - 'lowres'  → any blocked print (red)
 *  - 'ratio'   → any print that prints with a crop (auto or custom; amber)
 *  - 'ok'      → has prints, none of the above (green)
 *  - null      → no prints yet (plain card)
 */
export type PhotoStatus = 'lowres' | 'ratio' | 'ok' | null;

export function photoStatus(prints: UiPrint[]): PhotoStatus {
  if (prints.length === 0) return null;
  if (prints.some(isBlockedPrint)) return 'lowres';
  if (prints.some((p) => p.printCheck === 'autocrop' && p.fitMode === 'crop')) return 'ratio';
  return 'ok';
}

export interface PrintCheckSummary {
  /** Photos that have at least one print (i.e. were checked). */
  checkedPhotos: number;
  /** Unresolved ratio-mismatch prints / photos. */
  ratioPrintCount: number;
  ratioPhotoCount: number;
  /** Unresolved (blocked) low-res prints / photos. */
  lowresPrintCount: number;
  lowresPhotoCount: number;
  /** Photos with any unresolved flag (tray subtitle + legend button). */
  flaggedPhotoCount: number;
  /** Unresolved flagged prints (= tray rows). */
  flaggedIssueCount: number;
  /** Ratio prints already resolved (crop confirmed / custom / fit). */
  resolvedRatioCount: number;
  /** Distinct sizeIds among unresolved ratio prints (banner wording). */
  ratioSizeIds: string[];
}

export function summarizePrintCheck(prints: UiPrint[]): PrintCheckSummary {
  const photosWithPrints = new Set<string>();
  const ratioPhotos = new Set<string>();
  const lowresPhotos = new Set<string>();
  const flaggedPhotos = new Set<string>();
  const ratioSizeIds = new Set<string>();
  let ratioPrintCount = 0;
  let lowresPrintCount = 0;
  let resolvedRatioCount = 0;

  for (const p of prints) {
    photosWithPrints.add(p.photoId);
    if (isRatioUnresolved(p)) {
      ratioPrintCount += 1;
      ratioPhotos.add(p.photoId);
      flaggedPhotos.add(p.photoId);
      ratioSizeIds.add(p.sizeId);
    } else if (isRatioResolved(p)) {
      resolvedRatioCount += 1;
    }
    if (isLowresUnresolved(p)) {
      lowresPrintCount += 1;
      lowresPhotos.add(p.photoId);
      flaggedPhotos.add(p.photoId);
    }
  }

  return {
    checkedPhotos: photosWithPrints.size,
    ratioPrintCount,
    ratioPhotoCount: ratioPhotos.size,
    lowresPrintCount,
    lowresPhotoCount: lowresPhotos.size,
    flaggedPhotoCount: flaggedPhotos.size,
    flaggedIssueCount: ratioPrintCount + lowresPrintCount,
    resolvedRatioCount,
    ratioSizeIds: [...ratioSizeIds],
  };
}

/**
 * Live pre-add Print Check buckets for the Configure pane: photo ids grouped
 * by the status checkPrint gives them at the chosen size (silent default —
 * centered auto-crop). The chip COUNTS are these lengths and the chip FILTERS
 * are these id lists, so the two can never disagree (single source, no
 * duplicated thresholds).
 */
export interface PrecheckBuckets {
  ok: string[];
  autocrop: string[];
  lowres: string[];
}

export function precheckBuckets(
  photos: readonly Pick<LocalPhoto, 'id' | 'width' | 'height'>[],
  entry: ResolvedSize,
  thresholds: PrintCheckThresholds,
): PrecheckBuckets {
  const buckets: PrecheckBuckets = { ok: [], autocrop: [], lowres: [] };
  for (const ph of photos) {
    buckets[checkPrint(ph, entry.inches, null, 'crop', entry.family, thresholds).status].push(ph.id);
  }
  return buckets;
}

/* ============================================================
   Friendly DPI meter copy (tiered labels)
   ============================================================ */

/**
 * FOUR quality tiers (pricing v5):
 *   'best' ≥ targetDpi (300) · 'good' ≥ goodDpi (200) · 'ok' ≥ the family's
 *   minimum DPI (C-Type 100 / fine art 180) · 'low' below the minimum
 *   (blocked, override allowed).
 */
export type DpiTier = 'best' | 'good' | 'ok' | 'low';

/** Bucket an effective DPI into the friendly tier used by the crop meter + Print Check. */
export function dpiTier(
  dpi: number,
  family: PrintFamily,
  thresholds: PrintCheckThresholds = DEFAULT_THRESHOLDS,
): DpiTier {
  if (dpi >= thresholds.targetDpi) return 'best';
  if (dpi >= thresholds.goodDpi) return 'good';
  if (dpi >= minDpiOf(thresholds, family)) return 'ok';
  return 'low';
}

/** Customer-facing headline for a DPI tier (keep the number as small secondary text). */
export function dpiTierLabel(tier: DpiTier): string {
  return tier === 'best'
    ? 'Best quality'
    : tier === 'good'
      ? 'Good quality'
      : tier === 'ok'
        ? 'Prints OK at this size'
        : 'Too low for this size';
}

/** Maps a DPI tier to the app's status color kind. */
export function dpiTierKind(tier: DpiTier): 'ok' | 'warn' | 'err' {
  return tier === 'best' || tier === 'good' ? 'ok' : tier === 'ok' ? 'warn' : 'err';
}

/* ============================================================
   Border UI state machine (white / handling / edge)
   Borders do NOT change price — this is display + gate logic only.
   ============================================================ */

/** Short mode label (segment text). */
export function borderModeLabel(mode: BorderMode): string {
  return mode === 'white' ? 'White border' : mode === 'handling' ? 'Handling border' : 'Print to edge';
}

/** `25` mm → `2.5cm`, `4` mm → `4mm` (whole cm drops the decimal). */
export function borderMeasure(mm: number): string {
  if (mm >= 10) {
    const cm = mm / 10;
    return `${Number.isInteger(cm) ? cm : cm.toFixed(1)}cm`;
  }
  return `${mm}mm`;
}

/**
 * Full option label with the resolved measure, e.g. `White border (4mm)` /
 * `Handling border (2.5cm)`. `none` has no measure.
 */
export function borderOptionLabel(data: PricingData, sizeId: string, mode: BorderMode): string {
  if (mode === 'none') return 'Print to edge';
  const { mm } = resolveBorder(data, sizeId, mode);
  return `${borderModeLabel(mode)} (${borderMeasure(mm)})`;
}

/** One-line explainer for the info box, per mode. */
export function borderInfo(data: PricingData, sizeId: string, mode: BorderMode): string {
  if (mode === 'white') {
    const { mm } = resolveBorder(data, sizeId, mode);
    return `A clean white margin (${borderMeasure(mm)}) inside the print size. Your photo shrinks a little so nothing is cut off.`;
  }
  if (mode === 'handling') {
    const { mm } = resolveBorder(data, sizeId, mode);
    return `${borderMeasure(mm)} of blank paper around the print (a larger sheet) so you can mount or frame it yourself.`;
  }
  return 'We trim to the exact print size with no margin. Best for pre-made frames.';
}

export interface BorderUiState {
  /** Modes offered for this print (framed → ['none'] only). */
  modes: BorderMode[];
  /** The effective current mode (defaults to 'none'). */
  current: BorderMode;
  /** Whether this print participates in the fine-art Continue gate. */
  gated: boolean;
  /** True once a fine-art print's border choice has been made (gate satisfied). */
  decided: boolean;
}

/** Resolve the border control state for a print. */
export function borderUiState(print: UiPrint): BorderUiState {
  const framed = !!print.frameId;
  const modes = borderModesFor(print.sizeId, framed);
  const current = framed ? 'none' : (print.borderMode ?? 'none');
  const gated = !framed && parseSizeId(print.sizeId).family === 'fine_art';
  return { modes, current, gated, decided: !gated || !!print.borderDecided };
}

/** A fine-art print whose border choice is still pending (undecided). */
export function isFineArtBorderUndecided(print: UiPrint): boolean {
  const s = borderUiState(print);
  return s.gated && !s.decided;
}

/**
 * The border a size actually stores for a requested mode: the mode itself when
 * the size offers it, else 'none'. null/undefined (no explicit choice) → 'none'.
 */
export function effectiveBorderFor(sizeId: string, border: BorderMode | null | undefined): BorderMode {
  if (border == null) return 'none';
  return borderModesFor(sizeId, false).includes(border) ? border : 'none';
}

/**
 * THE loose-print merge key: photo × size × paper × border × B&W. Two prints
 * that differ only by borderMode are SEPARATE rows/chips (round-3 decision),
 * and a colour print never merges with a black & white one (round 6) — adding
 * an identical combo merges into the existing print's qty instead.
 */
export function findLooseMergeTarget(
  prints: readonly UiPrint[],
  photoId: string,
  sizeId: string,
  paper: PaperId,
  border: BorderMode | null | undefined,
  bw?: boolean,
  excludeId?: string,
): UiPrint | undefined {
  const eff = effectiveBorderFor(sizeId, border);
  const wantBw = bw ?? false;
  return prints.find(
    (p) =>
      p.id !== excludeId &&
      !p.frameId &&
      p.photoId === photoId &&
      p.sizeId === sizeId &&
      p.paper === paper &&
      (p.borderMode ?? 'none') === eff &&
      (p.bw ?? false) === wantBw,
  );
}

/** Undecided fine-art prints that are actually in the order (blocked low-res excluded). */
export function fineArtBorderGate(prints: UiPrint[]): UiPrint[] {
  return prints.filter((p) => !isBlockedPrint(p) && isFineArtBorderUndecided(p));
}

/** Does Continue need to force a border choice first? */
export function needsBorderGate(prints: UiPrint[]): boolean {
  return fineArtBorderGate(prints).length > 0;
}

/* ============================================================
   White-border geometry — CONTAIN, never crop
   ============================================================ */

/** A rectangle expressed as fractions [0,1] of an enclosing rectangle. */
export interface FracRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * White-border layout (the fix for "the border crops the image"). Given the
 * print's dimensions in the display orientation (inches) and the per-edge white
 * border in mm, return the rectangle — as fractions of the PRINT — that the
 * customer's cropped image occupies when SCALED TO CONTAIN within the content
 * area (print − 2·border), preserving the print aspect, centred. Nothing is
 * cropped: the whole cropped region shrinks and white makes up the difference.
 * Margins come out slightly uneven because the image keeps the print aspect while
 * the content box aspect differs a hair — "no crop" wins over "even border".
 */
export function whiteBorderImageRect(printWin: number, printHin: number, borderMm: number): FracRect {
  const bIn = borderMm / 25.4;
  const contentW = Math.max(0, printWin - 2 * bIn);
  const contentH = Math.max(0, printHin - 2 * bIn);
  if (contentW <= 0 || contentH <= 0) return { x: 0.5, y: 0.5, w: 0, h: 0 };
  const imgAspect = printWin / printHin; // crop keeps the print aspect
  const contentAspect = contentW / contentH;
  let w: number;
  let h: number;
  if (imgAspect > contentAspect) {
    w = contentW;
    h = contentW / imgAspect;
  } else {
    h = contentH;
    w = contentH * imgAspect;
  }
  const wf = w / printWin;
  const hf = h / printHin;
  return { x: (1 - wf) / 2, y: (1 - hf) / 2, w: wf, h: hf };
}

/**
 * CSS fractions that render the crop REGION of a photo so it exactly fills its
 * wrapper: the img is sized to 1/w × 1/h of the wrapper and offset so the
 * region's top-left lands at the wrapper's origin. Shared by the Configure
 * PrintPreview and the crop editor's white-border preview so both draw the
 * SAME geometry the bake produces (contain-shrink; the image gets smaller,
 * nothing is painted over it).
 */
export function cropRegionFracs(rect: CropRect): { width: number; height: number; left: number; top: number } {
  return {
    width: 1 / rect.w,
    height: 1 / rect.h,
    left: -rect.x / rect.w,
    top: -rect.y / rect.h,
  };
}

/**
 * Auto-crop TRIM OVERLAY geometry (shared by the photo-tile preview and the
 * Review-crops cards): the darkened strips outside a crop rect plus the kept
 * region's boundary, as percentage offset boxes over the image. Strips thinner
 * than 0.2% are dropped (nothing meaningful is trimmed on that edge). Pure
 * string geometry — callers spread the boxes into inline styles.
 */
export interface TrimBox {
  left?: string;
  right?: string;
  top?: string;
  bottom?: string;
  width?: string;
  height?: string;
}

export function trimOverlay(rect: CropRect): { strips: TrimBox[]; boundary: TrimBox } {
  const pct = (n: number) => `${(n * 100).toFixed(3)}%`;
  const strips: TrimBox[] = [];
  if (rect.x > 0.002) strips.push({ left: '0', top: '0', bottom: '0', width: pct(rect.x) });
  const right = 1 - rect.x - rect.w;
  if (right > 0.002) strips.push({ right: '0', top: '0', bottom: '0', width: pct(right) });
  if (rect.y > 0.002) strips.push({ top: '0', left: '0', right: '0', height: pct(rect.y) });
  const bottom = 1 - rect.y - rect.h;
  if (bottom > 0.002) strips.push({ bottom: '0', left: '0', right: '0', height: pct(bottom) });
  const boundary: TrimBox = {
    left: pct(rect.x),
    top: pct(rect.y),
    width: pct(rect.w),
    height: pct(rect.h),
  };
  return { strips, boundary };
}

/** Fraction of the whole (print + margin) sheet taken by a handling border on each edge. */
export function handlingBorderFrac(printWin: number, printHin: number, borderMm: number): { x: number; y: number } {
  const bMmToIn = borderMm / 25.4;
  return {
    x: bMmToIn / (printWin + 2 * bMmToIn),
    y: bMmToIn / (printHin + 2 * bMmToIn),
  };
}

/* ============================================================
   Rubber-band box selection (pure hit-testing)
   ============================================================ */

export interface PixelRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Build a normalized (non-negative w/h) rect from two drag corners. */
export function normalizeDragRect(x0: number, y0: number, x1: number, y1: number): PixelRect {
  return { x: Math.min(x0, x1), y: Math.min(y0, y1), w: Math.abs(x1 - x0), h: Math.abs(y1 - y0) };
}

/** Axis-aligned rectangle overlap test (touching edges do NOT count). */
export function rectsIntersect(a: PixelRect, b: PixelRect): boolean {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}

/**
 * Ids of the tiles whose rects intersect the selection box, in tile order.
 * STRICT 2D overlap — a tall narrow box only selects tiles it also overlaps
 * horizontally (never "everything in between" as an index range). Tiles with a
 * degenerate (zero-size) rect — e.g. layout skipped by content-visibility —
 * are ignored so they can't act as stray hit points.
 */
export function boxSelectHits(
  box: PixelRect,
  tiles: ReadonlyArray<{ id: string; rect: PixelRect }>,
): string[] {
  return tiles
    .filter((t) => t.rect.w > 0 && t.rect.h > 0 && rectsIntersect(box, t.rect))
    .map((t) => t.id);
}

/* ============================================================
   Per-print crop selection (each photo × size × paper has its own crop)
   ============================================================ */

/** The crop targets for a photo — one per loose print (framed prints excluded). */
export function cropTargetsForPhoto(prints: UiPrint[]): UiPrint[] {
  return prints.filter((p) => !p.frameId);
}

/** A stable key for a print's EFFECTIVE crop region (for the multi-crop indicator). */
function cropKey(print: UiPrint, photo: { width: number; height: number }, inches: Inches): string {
  if (print.fitMode === 'fit') return `fit@${print.rotation ?? 0}`;
  const [rw, rh] = rotatedDims(photo.width, photo.height, print.rotation ?? 0);
  const r: CropRect = print.cropRect ?? centeredAutoCropRect(rw, rh, inches);
  return `${print.rotation ?? 0}@${r.x.toFixed(3)}:${r.y.toFixed(3)}:${r.w.toFixed(3)}:${r.h.toFixed(3)}`;
}

/**
 * True when a photo's loose prints don't all share the same effective crop —
 * i.e. different sizes trim differently, or a custom crop diverges from a
 * sibling. Drives the small "multiple crops" indicator on the photo card.
 */
export function photoHasMixedCrops(
  prints: UiPrint[],
  photo: { width: number; height: number },
  catalog: Map<string, ResolvedSize>,
): boolean {
  const loose = cropTargetsForPhoto(prints);
  if (loose.length < 2) return false;
  let first: string | null = null;
  for (const p of loose) {
    const entry = catalog.get(p.sizeId);
    if (!entry) continue;
    const key = cropKey(p, photo, entry.inches);
    if (first === null) first = key;
    else if (key !== first) return true;
  }
  return false;
}

/* ============================================================
   Print construction (shared by the apply bar + the Easy Print Wizard)
   ============================================================ */

/**
 * Build the fields of a fresh loose print for photo×size×paper, with Print Check
 * applied — the single source of truth used by both the Configure "Add" path and
 * the wizard's bulk-apply. Pure (no id/uid), so it is unit-testable; callers add
 * the client id. `border` stamps a chosen border when the size offers it.
 */
export function buildPrintFields(
  photo: { id: string; width: number; height: number },
  sizeId: string,
  paper: PaperId,
  qty: number,
  border: BorderMode | null | undefined,
  entry: ResolvedSize,
  data: PricingData,
  thresholds: PrintCheckThresholds = DEFAULT_THRESHOLDS,
): Omit<UiPrint, 'id'> {
  const result = checkPrint(photo, entry.inches, null, 'crop', entry.family, thresholds);
  const chosen = border != null && borderModesFor(sizeId, false).includes(border);
  return {
    printId: null,
    photoId: photo.id,
    sizeId,
    paper,
    qty,
    fitMode: 'crop',
    cropRect: null,
    printCheck: result.status,
    overrideLowRes: false,
    effectiveDpi: result.dpi,
    unitPrice: priceForSafe(data, sizeId, paper),
    borderMode: chosen ? border : 'none',
    rotation: 0,
    bw: false,
    // C-Type never gates; a fine-art print is decided only once a border is chosen.
    borderDecided: entry.family === 'ctype' ? true : chosen,
  };
}

/**
 * Wizard bulk-apply builder: one config → a print for every READY photo in the
 * given list (auto-crop). Pure; callers add client ids and choose the SCOPE by
 * pre-filtering the photo list (see useOrderActions.bulkApplyAll: 'printless'
 * appends to photos without prints; 'all' replaces the whole order).
 */
export function buildBulkPrints(
  photos: LocalPhoto[],
  sizeId: string,
  paper: PaperId,
  qty: number,
  border: BorderMode | null | undefined,
  catalog: Map<string, ResolvedSize>,
  data: PricingData,
  thresholds: PrintCheckThresholds = DEFAULT_THRESHOLDS,
): Array<Omit<UiPrint, 'id'>> {
  const entry = catalog.get(sizeId);
  if (!entry) return [];
  return photos
    .filter((p) => p.status === 'ready')
    .map((p) => buildPrintFields(p, sizeId, paper, qty, border, entry, data, thresholds));
}

/* ============================================================
   Photo sorting (mirrors the upload screen's Sort control)
   ============================================================ */

/**
 * Sort photos for a grid. The Step-2 default is `'todo'`: photos WITHOUT a print
 * float to the top (what still needs configuring) and photos WITH a print sink to
 * the bottom, each group holding its upload order. `photosWithPrints` is the set
 * of photo ids that carry ≥1 print; omit it for the non-todo sorts (upload / name
 * / largest), which ignore it.
 */
export function sortPhotos(
  photos: LocalPhoto[],
  sort: SortMode,
  photosWithPrints?: ReadonlySet<string>,
): LocalPhoto[] {
  const arr = photos.slice();
  if (sort === 'todo') {
    const has = photosWithPrints ?? EMPTY_ID_SET;
    // Stable partition: no-prints (0) before has-prints (1), upload order within.
    arr.sort((a, b) => {
      const rank = (has.has(a.id) ? 1 : 0) - (has.has(b.id) ? 1 : 0);
      return rank !== 0 ? rank : a.sortOrder - b.sortOrder;
    });
  } else if (sort === 'name') arr.sort((a, b) => a.filename.localeCompare(b.filename));
  else if (sort === 'mp-desc') arr.sort((a, b) => b.mp - a.mp);
  else arr.sort((a, b) => a.sortOrder - b.sortOrder);
  return arr;
}

const EMPTY_ID_SET: ReadonlySet<string> = new Set<string>();

/** Photo ids that carry at least one print (drives the 'todo' sort + tile state). */
export function photosWithPrintsSet(prints: UiPrint[]): Set<string> {
  const out = new Set<string>();
  for (const p of prints) out.add(p.photoId);
  return out;
}

/* ============================================================
   Suggested print size (Configure chip)
   ============================================================ */

/**
 * Popularity-ordered C-Type sizes eligible for the suggestion chip — a
 * sensible enlargement, capped at 8×12 (no fine-art aspirations here).
 */
const SUGGEST_POPULAR: readonly string[] = [
  '4x6',
  '5x7',
  '6x8',
  '8x10',
  '8x12',
  '6x9',
  '5x7.5',
  '6x6',
  '8x8',
  '5x5',
  '3.5x5',
];

export interface SizeSuggestion {
  sizeId: string;
  entry: ResolvedSize;
  /** Effective DPI of the FULL image at this size. */
  dpi: number;
  /** Photo aspect within tolerance of the print — a "great fit". */
  aspectClose: boolean;
}

/** Largest print area wins; popularity order breaks ties. */
function largestSuggestion(list: SizeSuggestion[]): SizeSuggestion {
  let best = list[0]!;
  let bestArea = best.entry.inches[0] * best.entry.inches[1];
  for (const c of list.slice(1)) {
    const area = c.entry.inches[0] * c.entry.inches[1];
    if (area > bestArea) {
      best = c;
      bestArea = area;
    }
  }
  return best;
}

/**
 * Suggest a print size for a photo (the Configure "Suggested" chip). A size
 * that Print Check would BLOCK (auto-crop DPI < its family's minimum) is never
 * suggested — the chip must always be addable as-is. When `data` + `paper`
 * are given and the paper is a C-Type finish, sizes that finish can't print
 * (per-size restriction, e.g. 8×12 on Gloss) are skipped too — the chip never
 * proposes a size that would silently switch the chosen finish. Quality bands
 * in order:
 *  1. sizes at ≥ targetDpi (300, "best") — aspect-close LARGEST first, else the
 *     most popular;
 *  2. else sizes at ≥ goodDpi (200, "good") — same preference;
 *  3. else whatever still clears its family minimum (never blocked), preferring
 *     aspect-close ones, largest;
 *  4. else null (nothing sensible to suggest).
 */
export function suggestSize(
  photo: { width: number; height: number },
  catalog: Map<string, ResolvedSize>,
  thresholds: PrintCheckThresholds = DEFAULT_THRESHOLDS,
  data?: PricingData,
  paper?: PaperId,
): SizeSuggestion | null {
  if (!photo.width || !photo.height) return null;
  const candidates: SizeSuggestion[] = [];
  for (const size of SUGGEST_POPULAR) {
    const entry = catalog.get(ctypeSizeId(size));
    if (!entry) continue;
    // Skip sizes the current C-Type finish can't print (e.g. 8×12 on Gloss).
    if (data && paper && isCtPaper(data, paper) && !isPaperValidForSize(data, entry.sizeId, paper)) {
      continue;
    }
    // The suggestion must never be blocked: run the same check the Add path runs
    // (centered auto-crop, per-family minimum DPI).
    const blocked =
      checkPrint(photo, entry.inches, null, 'crop', entry.family, thresholds).status === 'lowres';
    if (blocked) continue;
    const dpi = effectiveDpi(photo.width, photo.height, [entry.inches[0], entry.inches[1]]);
    const aspectClose =
      ratioFit(photo.width, photo.height, entry.inches, thresholds.ratioTolerance) === 'exact';
    candidates.push({ sizeId: entry.sizeId, entry, dpi, aspectClose });
  }
  if (candidates.length === 0) return null;
  // Quality bands: best (≥300) first, then good (≥200), then the rest (≥ min).
  for (const floor of [thresholds.targetDpi, thresholds.goodDpi]) {
    const band = candidates.filter((c) => c.dpi >= floor);
    const close = band.filter((c) => c.aspectClose);
    if (close.length > 0) return largestSuggestion(close);
    if (band.length > 0) return band[0]!;
  }
  // Everything left prints ≥ its family minimum (blocked sizes were skipped).
  const okClose = candidates.filter((c) => c.aspectClose);
  return largestSuggestion(okClose.length > 0 ? okClose : candidates);
}

/* ============================================================
   Easy Print Wizard visibility (Step-2 chip + auto-offer)
   ============================================================ */

/** Large-batch threshold at/above which the Easy Print Wizard is offered. */
export const WIZARD_PHOTO_THRESHOLD = 40;

/** The persistent "✨ Bulk order?" wizard chip shows whenever ≥40 photos are ready. */
export function wizardChipVisible(readyCount: number): boolean {
  return readyCount >= WIZARD_PHOTO_THRESHOLD;
}

/**
 * AUTO-offer predicate (round 5): the wizard prompts EVERY time Step 2 mounts
 * with a large batch (≥40 ready photos) while at least one ready photo still
 * has NO prints — i.e. the user arrived with newly-added photos since the
 * wizard last ran. Once every photo carries a print the auto-prompt stops;
 * the persistent chip remains either way.
 */
export function wizardAutoPrompt(readyPhotoIds: readonly string[], prints: readonly UiPrint[]): boolean {
  if (readyPhotoIds.length < WIZARD_PHOTO_THRESHOLD) return false;
  const withPrints = new Set<string>();
  for (const p of prints) withPrints.add(p.photoId);
  return readyPhotoIds.some((id) => !withPrints.has(id));
}

/** What the wizard's apply touches: only printless photos, or the whole order. */
export type WizardScope = 'printless' | 'all';

export interface WizardScopePlan {
  scope: WizardScope;
  /** The photo ids the apply will configure. */
  targetIds: string[];
  /** True when other photos already carry prints that the apply will NOT touch. */
  preservesExisting: boolean;
  /** True when the apply REPLACES an existing full order (explicit re-run). */
  replacesAll: boolean;
}

/**
 * Wizard scope rules (round 6):
 *  - no photo has prints → fresh setup, applies to every ready photo;
 *  - SOME photos have prints → applies ONLY to the printless photos, existing
 *    prints stay untouched (the summary says so);
 *  - EVERY photo has prints (an explicit chip re-run) → replace-all, and the
 *    summary asks "Replace everything with this new setup?".
 */
export function wizardScopeFor(
  readyPhotoIds: readonly string[],
  prints: readonly UiPrint[],
): WizardScopePlan {
  const withPrints = new Set<string>();
  for (const p of prints) withPrints.add(p.photoId);
  const printless = readyPhotoIds.filter((id) => !withPrints.has(id));
  const hasAnyPrint = prints.length > 0;
  if (printless.length === 0 && hasAnyPrint) {
    return { scope: 'all', targetIds: [...readyPhotoIds], preservesExisting: false, replacesAll: true };
  }
  return {
    scope: 'printless',
    targetIds: printless.length > 0 ? printless : [...readyPhotoIds],
    preservesExisting: hasAnyPrint && printless.length > 0,
    replacesAll: false,
  };
}

/**
 * How many of the photos don't match a print size's aspect (within tolerance)
 * and would therefore be auto-cropped — the wizard summary flags this BEFORE
 * apply ("12 of 59 photos don't match 4×6 and will be auto-cropped").
 */
export function countRatioMismatch(
  photos: ReadonlyArray<{ width: number; height: number }>,
  inches: Inches,
  tolerance = DEFAULT_THRESHOLDS.ratioTolerance,
): number {
  let n = 0;
  for (const p of photos) {
    if (p.width > 0 && p.height > 0 && ratioFit(p.width, p.height, inches, tolerance) === 'autocrop') n += 1;
  }
  return n;
}

/**
 * Whether the editor offers the "Fit (adds white space)" mode at all: hidden
 * when the photo already matches the print ratio (within tolerance) because
 * fitting would change nothing. Orientation-agnostic, so rotation is
 * irrelevant to the answer.
 */
export function fitOptionVisible(
  photoW: number,
  photoH: number,
  inches: Inches,
  tolerance = DEFAULT_THRESHOLDS.ratioTolerance,
): boolean {
  if (!(photoW > 0) || !(photoH > 0)) return true;
  return ratioFit(photoW, photoH, inches, tolerance) === 'autocrop';
}

/* ============================================================
   Stocked frame configs (Configure "Frame" dropdown)
   ============================================================ */

export interface FrameConfigOption {
  frameId: string;
  /** sizeId of the INCLUDED print (guaranteed to exist in the catalog). */
  sizeId: string;
  printSize: string;
  frameSize: string;
  borderLabel: string;
  price: number;
  /** Paper of the coupled framed product — may DIFFER from the customer's. */
  paper: PaperId;
  /** `8×10" print in 8×10" frame · Print-to-Edge` (+ ` · {paper}` when it differs). */
  label: string;
  /** Frame colour key, e.g. 'black' | 'white'. */
  colour: string;
  /** Customer-facing colour word, e.g. "White". */
  colourLabel: string;
}

/**
 * ANY-PAPER upsell list (owner 2026-07-26): ONE framed product per stocked
 * config (print size → frame size · border), whatever the customer's current
 * paper — every frame is an upsell, so every frame size is always on offer.
 * The customer's own paper is used where that config stocks it; otherwise the
 * CHEAPEST stocked paper stands in (the framed price includes the print, so
 * no new pricing is needed — picking it converts the print's size/paper with
 * a warning). Configs whose print size isn't a stocked loose size are skipped
 * (they can't be priced/Print-Checked). Sorted smallest print size first.
 */
export function framedUpsellProducts(
  data: PricingData,
  catalog: Map<string, ResolvedSize>,
  paper: PaperId,
  colour?: string,
): FramedProduct[] {
  // The chooser shows ONE colour's worth of configs; colour is picked by its
  // own control, not by doubling this list. Default to the unsuffixed colour
  // so a caller that predates colour behaves exactly as it used to.
  const wanted = colour ?? defaultFrameColour(data);
  const groups = new Map<string, FramedProduct[]>();
  for (const f of framedProductList(data)) {
    if (f.colour !== wanted) continue;
    const key = frameConfigKey(f);
    const g = groups.get(key);
    if (g) g.push(f);
    else groups.set(key, [f]);
  }
  const out: FramedProduct[] = [];
  for (const g of groups.values()) {
    const chosen =
      g.find((f) => f.paper === paper) ?? g.reduce((a, b) => (b.price < a.price ? b : a));
    const sizeId = (isCtPaper(data, chosen.paper) ? ctypeSizeId : fineArtSizeId)(chosen.printSize);
    if (!catalog.has(sizeId)) continue;
    out.push(chosen);
  }
  // Order smallest to largest by actual PRINT AREA, using the resolved inches
  // rather than re-parsing the size name — "A3".split('x') is [NaN], which put
  // every A-series frame in an arbitrary slot when the A2 frame shipped
  // (owner 2026-08-28). By area A3 (193 sq in) lands between 12x16 (192) and
  // 12x18 (216), which is where a customer expects to find it.
  const area = (f: FramedProduct): number => {
    const [w, h] = f.printInches;
    return Number.isFinite(w) && Number.isFinite(h) ? w * h : Number.MAX_SAFE_INTEGER;
  };
  const shortEdge = (f: FramedProduct): number => Math.min(...f.printInches);
  return out.sort((p, q) => area(p) - area(q) || shortEdge(p) - shortEdge(q));
}

/** FramedProduct → display option (label carries the paper when it differs). */
function toFrameConfigOption(
  data: PricingData,
  f: FramedProduct,
  paper: PaperId,
): FrameConfigOption {
  const sizeId = (isCtPaper(data, f.paper) ? ctypeSizeId : fineArtSizeId)(f.printSize);
  const borderLabel = data.productLines.framing.borderStyles[f.border] ?? f.border;
  return {
    frameId: f.frameId,
    sizeId,
    printSize: f.printSize,
    frameSize: f.frameSize,
    borderLabel,
    price: f.price,
    paper: f.paper,
    label:
      `${sizeWithUnit(f.printSize)} print in ${sizeWithUnit(f.frameSize)} frame · ${borderLabel}` +
      (f.paper !== paper ? ` · ${framePaperLabel(data, f.paper)}` : ''),
    colour: f.colour,
    colourLabel: f.colourLabel,
  };
}

/**
 * The Configure/editor "Frame" dropdown options — framedUpsellProducts with
 * display labels (cross-paper options carry the paper name so the switch is
 * visible before picking).
 */
export function frameConfigOptions(
  data: PricingData,
  catalog: Map<string, ResolvedSize>,
  paper: PaperId,
  colour?: string,
): FrameConfigOption[] {
  return framedUpsellProducts(data, catalog, paper, colour).map((f) =>
    toFrameConfigOption(data, f, paper),
  );
}

/** Frame colours for the chooser, default (black) first. */
export function frameColourOptions(
  data: PricingData,
): Array<{ id: string; label: string; skuCode: string }> {
  return [...frameColours(data)].sort((a, b) => Number(!!a.skuCode) - Number(!!b.skuCode));
}

/** The default (unsuffixed) frame colour id for this pricing version. */
export function defaultFrameColour(data: PricingData): string {
  return (frameColours(data).find((c) => !c.skuCode) ?? frameColours(data)[0]!).id;
}

/**
 * The same frame config in another colour. Returns null when that colour does
 * not stock this configuration, so the caller can leave the pick alone rather
 * than guessing.
 */
export function frameInColour(
  data: PricingData,
  frameId: string,
  colour: string,
): FramedProduct | null {
  let self: FramedProduct;
  try {
    self = framedProduct(data, frameId);
  } catch {
    return null;
  }
  if (self.colour === colour) return self;
  const want = frameConfigKey({ ...self, colour });
  return (
    framedProductList(data).find((f) => frameConfigKey(f) === want && f.paper === self.paper) ??
    framedProductList(data).find((f) => frameConfigKey(f) === want) ??
    null
  );
}

/**
 * Resolve ANY frameId to a display option — e.g. a paper SIBLING picked via
 * the frame-paper picker, which isn't the upsell list's winner for its
 * config. Null when the id no longer resolves (pricing change).
 */
export function frameConfigOptionFor(
  data: PricingData,
  frameId: string,
  paper: PaperId,
): FrameConfigOption | null {
  try {
    return toFrameConfigOption(data, framedProduct(data, frameId), paper);
  } catch {
    return null;
  }
}

/**
 * The papers a picked frame CONFIG comes in — its sibling framed products
 * (same print size → frame size · border across every tier that stocks it).
 * Once a frame locks the size (owner 2026-07-27), THIS is the customer's
 * paper choice: picking a sibling switches the framed product/price.
 * Sorted cheapest first.
 */
export function framedPaperSiblings(data: PricingData, frameId: string): FramedProduct[] {
  let self: FramedProduct;
  try {
    self = framedProduct(data, frameId);
  } catch {
    return [];
  }
  // frameConfigKey includes COLOUR, so picking a paper can never silently
  // flip a white frame to black.
  const key = frameConfigKey(self);
  return framedProductList(data)
    .filter((f) => frameConfigKey(f) === key)
    .sort((p, q) => p.price - q.price || p.paper.localeCompare(q.paper));
}
